import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/nextjs';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { userPermissionsAtom, userRolesAtom } from '@/atoms/auth';
import { removeAuthToken } from '@/auth';
import { executeLogout } from '@/lib/auth/executeLogout';
import { postApiV1UserLogout } from '@/open-api/user/user';
import { logEvent } from '@/pages/api/v1/utils';
import { addClarityEvent } from '@/utils/analytics/microsoft/addClarityEvent';

import { useSettings } from './state/useSettings';

import type { Permission, Role } from '@/types/auth';

export const useLogout = () => {
  const { invalidateSettings } = useSettings();
  const { asPath } = useRouter();
  const queryClient = useQueryClient();
  const setUserRoles = useSetAtom(userRolesAtom);
  const setUserPermissions = useSetAtom(userPermissionsAtom);

  const logout = useCallback(async () => {
    // Track logout events (failures won't block logout)
    try {
      void logEvent({ event_type: 'logout', asPath });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { asPath, event_type: 'logout' },
      });
    }

    try {
      addClarityEvent('logout_completed');
    } catch (error) {
      Sentry.captureException(error, {
        extra: { event_type: 'logout_completed' },
      });
    }

    await executeLogout(
      {
        removeToken: () => removeAuthToken(),
        clearSessionStorage: () => sessionStorage.clear(),
        resetAuthAtoms: () => {
          setUserRoles(new Set<Role>(['Free']));
          setUserPermissions(new Set<Permission>());
        },
        invalidateQueryCache: () =>
          queryClient.invalidateQueries({ refetchType: 'none' }),
        callLogoutApis: async () => {
          const names = ['symphony logout', 'dotnet logout'];
          const results = await Promise.allSettled([
            postApiV1UserLogout(),
            fetch('/api/logout'),
          ]);
          return results.map((result, i) => ({ name: names[i]!, result }));
        },
        clearLegacyState: () => {
          invalidateSettings();
        },
        redirect: (url) => window.location.assign(url),
        reportError: (error, context) =>
          Sentry.captureException(error, { extra: context }),
      },
      {
        origin: window.location.origin,
        beBaseUrl:
          process.env.NEXT_PUBLIC_BE_BASE_URL ?? 'https://api.uppbeat.io',
      }
    );
  }, [
    invalidateSettings,
    queryClient,
    asPath,
    setUserRoles,
    setUserPermissions,
  ]);

  return { logout };
};
